import type { SerializedError } from '@reduxjs/toolkit'

import { isTransportError, type TransportError, type TransportErrorKind } from './transport'

/**
 * Состояние запроса глазами экрана (F06): загрузка, пусто, нет доступа,
 * служба недоступна и ошибка — РАЗНЫЕ состояния, а не один красный текст.
 * Селекторы vm пакетов строят его отсюда, чтобы каждый экран не толковал
 * `isLoading/isError/status` по-своему.
 */
export type QueryStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'empty'
  | 'unauthorized'
  | 'forbidden'
  | 'unavailable'
  | 'error'

export interface ErrorView {
  kind: TransportErrorKind | 'client'
  /** Текст сервера дословно (ADR-8) или понятный текст вида ошибки. */
  message: string
  code: string
  correlationId: string | null
  /** HTTP-статус ответа, если он был: нужен, чтобы отличить отказ до службы (413 прокси) от потерянного ответа. */
  status?: number
}

export interface QueryView {
  status: QueryStatus
  /** Данные есть, но идёт перечитывание: показывать их можно, помечая «обновляется». */
  refreshing: boolean
  error: ErrorView | null
}

/**
 * Минимум полей результата RTK Query, который нужен vm, — ровно то, что
 * возвращает `endpoint.select(args)(state)`. `isLoading` здесь в смысле
 * select: запрос В ПОЛЁТЕ (в том числе перечитывание при уже полученных данных).
 */
export interface QueryResultLike<T> {
  data?: T
  error?: TransportError | SerializedError
  isUninitialized: boolean
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
}

export function errorView(error: unknown): ErrorView {
  if (isTransportError(error)) {
    return {
      kind: error.kind,
      message: error.message,
      code: error.code,
      correlationId: error.correlationId,
      ...(error.status !== undefined ? { status: error.status } : {}),
    }
  }
  const message =
    typeof error === 'object' && error !== null && typeof (error as SerializedError).message === 'string'
      ? ((error as SerializedError).message ?? 'Ошибка клиента')
      : 'Ошибка клиента'
  return { kind: 'client', message, code: 'client', correlationId: null }
}

/**
 * Этот же запрос можно отправить ещё раз тем же ключом (transport пометил `retryable`): сбой связи или 5xx идемпотентной команды
 * (`TransportRequest.idempotent`) либо безопасного чтения. 4xx по существу (409, 403, 422) — нет: повтор вернёт тот же отказ.
 */
export function isRetryableError(error: unknown): boolean {
  return isTransportError(error) && error.retryable
}

function statusOfError(error: ErrorView): QueryStatus {
  if (error.kind === 'unauthorized') return 'unauthorized'
  if (error.kind === 'forbidden') return 'forbidden'
  if (error.kind === 'unavailable' || error.kind === 'network' || error.kind === 'timeout') return 'unavailable'
  return 'error'
}

/**
 * Вид запроса. `isEmpty` решает пакет: пустой список и пустой объект — разные
 * вещи. Ошибка при уже полученных данных показывается ошибкой, а не старыми
 * данными как актуальными (F06).
 */
export function queryView<T>(result: QueryResultLike<T>, isEmpty: (data: T) => boolean): QueryView {
  if (result.isError && result.error !== undefined) {
    const error = errorView(result.error)
    return { status: statusOfError(error), refreshing: false, error }
  }
  if (result.isUninitialized) return { status: 'idle', refreshing: false, error: null }
  if (result.data === undefined) return { status: 'loading', refreshing: false, error: null }
  return { status: isEmpty(result.data) ? 'empty' : 'ready', refreshing: result.isLoading, error: null }
}
